import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  ModalFooter,
  Button,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  IconButton,
  Spacer,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useResource } from "hooks/resources/providers/resourceProvider";
import { useState } from "react";
import { FieldErrors, FieldValues, UseFormReturn } from "react-hook-form";
import { DataTableFormConfig } from "./types/data-table-form.config";

type DataTableFormProps<
  T extends FieldValues & { id: string },
  TCreateViewModel extends Partial<T>,
  TUpdateViewModel extends Partial<T>
> = {
  resource: string;
  children: React.ReactNode;
  form: UseFormReturn<TCreateViewModel | TUpdateViewModel>;
  config?: Partial<DataTableFormConfig>;
};

export function DataTableForm<
  T extends FieldValues,
  TCreateViewModel extends Partial<T>,
  TUpdateViewModel extends Partial<T>
>({
  resource,
  children,
  form,
  config,
}: DataTableFormProps<T & { id: string }, TCreateViewModel, TUpdateViewModel>) {
  const configuration = {
    size: "lg",
    resourceName: resource,
    ...config,
  };

  const { actions, modal } = useResource<T>();

  const [startDelete, setStartDelete] = useState(false);

  const item = modal.selectedOne;

  const onSubmit = async (viewModel: TCreateViewModel | TUpdateViewModel) => {
    const data = {
      ...item,
      ...viewModel,
    };

    if (item !== null) {
      await actions.updateItem(item.id, data);
    } else {
      await actions.createItem(data);
    }

    modal.handleCloseModal();
  };

  const onError = (errors: FieldErrors<TCreateViewModel | TUpdateViewModel>) => {
    console.log("Validation errors", errors);
  };

  const isNew = item === null;

  const title = isNew
    ? `Create ${configuration.resourceName}`
    : `Edit ${configuration.resourceName}`;

  const submitButtonText = isNew ? `Add ${configuration.resourceName}` : `Save Changes`;

  const [isDeleting, setIsDeleting] = useState(false);

  async function handleDeleteField(object: T) {
    setIsDeleting(true);
    await actions.deleteItem(object.id);
    modal.handleCloseModal();
  }

  return (
    <Modal isOpen={modal.isOpen} onClose={modal.handleCloseModal} size={configuration.size}>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={form.handleSubmit(onSubmit, onError)} noValidate>
        <ModalHeader textTransform="capitalize">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Flex} flexDirection="column" gap={4}>
          {children}

          {startDelete && (
            <Alert status="error" borderRadius="8px">
              <AlertIcon />
              <Box>
                <AlertTitle textTransform="capitalize">
                  Delete {configuration.resourceName}?
                </AlertTitle>
                <AlertDescription>
                  Deleting a {configuration.resourceName} will remove it from the system.
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          {!startDelete ? (
            <>
              {!isNew && (
                <IconButton
                  variant="outline"
                  onClick={() => {
                    setStartDelete(true);
                  }}
                  aria-label="delete-field"
                  icon={<FontAwesomeIcon icon={["far", "trash-can"]} />}
                />
              )}
              <Spacer />
              <Flex gap={2}>
                <Button variant="outline" onClick={modal.handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  textTransform="capitalize"
                  isLoading={form.formState.isSubmitting}
                  leftIcon={<FontAwesomeIcon icon={["far", "save"]} />}
                >
                  {submitButtonText}
                </Button>
              </Flex>
            </>
          ) : (
            <Flex gap={2}>
              <Button
                variant="outline"
                onClick={() => {
                  setStartDelete(false);
                }}
              >
                Not Now
              </Button>
              <Button
                variant="red"
                onClick={() => handleDeleteField(item!)}
                isLoading={isDeleting}
                leftIcon={<FontAwesomeIcon icon={["far", "trash-alt"]} />}
              >
                Confirm
              </Button>
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
